import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import SEO from "../../components/SEO";
import DeployLegalFooter from "../../components/deploy/DeployLegalFooter";

const publicApi = axios.create({
  baseURL: "",
  headers: { "Content-Type": "application/json" },
});

/**
 * RD-0731 — public abuse / DMCA intake form → POST /api/deploy/legal/abuse-intake
 */
function DeployAbuseReportPage() {
  const [category, setCategory] = useState("abuse");
  const [subject, setSubject] = useState("");
  const [details, setDetails] = useState("");
  const [reporterEmail, setReporterEmail] = useState("");
  const [reportedUrl, setReportedUrl] = useState("");
  const [companyWebsite, setCompanyWebsite] = useState("");
  const [status, setStatus] = useState({ kind: "idle", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    console.log(`[${new Date().toISOString()}] [DeployAbuseReportPage] mount`);
  }, []);

  useEffect(() => {
    console.log(`[${new Date().toISOString()}] [DeployAbuseReportPage] render`);
  });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus({ kind: "idle", message: "" });
    const t0 = Date.now();
    try {
      const { data } = await publicApi.post("/api/deploy/legal/abuse-intake", {
        category,
        subject: subject.trim(),
        details: details.trim(),
        reporterEmail: reporterEmail.trim(),
        reportedUrl: reportedUrl.trim(),
        companyWebsite,
      });
      console.log(
        `[${new Date().toISOString()}] [DeployAbuseReportPage] submit ok ms=${Date.now() - t0} success=${data.success}`
      );
      setStatus({ kind: "ok", message: data.message || "Received." });
      setDetails("");
      setSubject("");
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        "Could not submit. Try again later or email support if configured.";
      console.error(`[${new Date().toISOString()}] [DeployAbuseReportPage] submit error`, msg);
      setStatus({ kind: "err", message: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Limelite Deploy — Abuse & DMCA reports"
        description="Report abuse, copyright, or other policy violations related to RivieraDeploy. EUR billing unchanged."
        keywords="Limelite Deploy, abuse, DMCA, report"
        url="/limelite/deploy/abuse-report"
      />
      <div className="bg-black text-silver-light min-h-[calc(100vh-4rem)] px-4 py-12">
        <div className="max-w-xl mx-auto space-y-8">
          <div>
            <p className="text-sm uppercase tracking-widest text-lime-500 text-xs uppercase tracking-widest">Limelite Deploy</p>
            <h1 className="text-3xl font-serif font-medium mt-2">Abuse &amp; DMCA intake</h1>
            <p className="text-silver-medium text-sm mt-3">
              Use this form for abuse reports, copyright / IP notices, and other policy issues. Do not paste passwords or API
              keys. We aim to triage within 24 business hours where feasible.
            </p>
          </div>

          {status.kind === "ok" && (
            <div className="bg-slate-900/80 border border-slate-700 rounded-lg px-4 py-3 text-sm text-slate-100" role="status">
              {status.message}
            </div>
          )}
          {status.kind === "err" && (
            <div className="bg-red-950/50 border border-red-900 rounded-lg px-4 py-3 text-sm text-red-200" role="alert">
              {status.message}
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-4 bg-off-black border border-gray-800 rounded-lg p-6">
            <div>
              <label className="block text-sm text-silver-medium mb-1">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full bg-black border border-gray-700 rounded px-3 py-2 text-sm text-silver-light"
              >
                <option value="abuse">Abuse / security</option>
                <option value="dmca">Copyright or IP (DMCA-style)</option>
                <option value="other">Other policy</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-silver-medium mb-1">Subject (optional)</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={200}
                className="w-full bg-black border border-gray-700 rounded px-3 py-2 text-sm text-silver-light"
              />
            </div>
            <div>
              <label className="block text-sm text-silver-medium mb-1">Details (required, min 10 characters)</label>
              <textarea
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                rows={8}
                required
                minLength={10}
                maxLength={8000}
                className="w-full bg-black border border-gray-700 rounded px-3 py-2 text-sm text-silver-light font-mono"
              />
            </div>
            <div>
              <label className="block text-sm text-silver-medium mb-1">Reported URL or resource (optional)</label>
              <input
                type="url"
                value={reportedUrl}
                onChange={(e) => setReportedUrl(e.target.value)}
                placeholder="https://"
                className="w-full bg-black border border-gray-700 rounded px-3 py-2 text-sm text-silver-light"
              />
            </div>
            <div>
              <label className="block text-sm text-silver-medium mb-1">Your email (optional, for follow-up)</label>
              <input
                type="email"
                value={reporterEmail}
                onChange={(e) => setReporterEmail(e.target.value)}
                className="w-full bg-black border border-gray-700 rounded px-3 py-2 text-sm text-silver-light"
              />
            </div>

            <div className="hidden" aria-hidden="true">
              <label htmlFor="deploy-company-website">Company website</label>
              <input
                id="deploy-company-website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={companyWebsite}
                onChange={(e) => setCompanyWebsite(e.target.value)}
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full sm:w-auto bg-black border border-silver-medium hover:bg-silver-medium hover:text-black text-silver-light font-medium py-2 px-6 rounded-md transition disabled:opacity-50"
            >
              {submitting ? "Sending…" : "Submit report"}
            </button>
          </form>

          <p className="text-xs text-gray-600">
            Operator runbook: <code className="text-gray-500">docs/deploy/legal/abuse-dmca.md</code>
          </p>

          <Link to="/limelite/deploy" className="inline-block text-sm text-silver-medium hover:text-silver-light underline">
            Back to Limelite Deploy
          </Link>

          <DeployLegalFooter />
        </div>
      </div>
    </>
  );
}

export default DeployAbuseReportPage;
